import type { Insurance } from "../machine";

/**
 * Active patient-identification mode in the scheduler flow.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * const mode: IdentificationMode = 'card'
 * ```
 */
export type IdentificationMode = "card" | "search" | "create";

/**
 * Returns a patient ID only when the card workflow authoritatively resolved one.
 *
 * A `MATCHING` result still requires an explicit inSuite selection and its card-read log ID is
 * never a patient identity.
 *
 * @since 1.1.7
 * @example
 * ```ts
 * const patientId = resolvedCardPatientId({ status: 'APPLIED', patientId: 'patient-1' })
 * ```
 */
export function resolvedCardPatientId(input: {
  readonly status: string;
  readonly patientId?: string;
}): string | undefined {
  return input.status === "APPLIED" || input.status === "MERGED"
    ? input.patientId
    : undefined;
}

/**
 * Maps bridge insurance codes to scheduler insurance values.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * const insurance = mapInsuranceType('private')
 * ```
 */
export function mapInsuranceType(type?: string): Insurance | undefined {
  switch (type) {
    case "statutory":
      return "GKV";
    case "private":
      return "PKV";
    case "self-pay":
      return "SELFPAY";
    default:
      return undefined;
  }
}
